import React, { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Skeleton,
  Grid,
  Button,
  Stack,
  Paper,
  alpha,
  useTheme,
  Chip,
  Avatar,
} from "@mui/material";
import {
  AddOutlined,
  AssessmentOutlined,
  Cake,
  Badge as BadgeIcon,
  LocationOn,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";

import {
  fetchInitialAssessmentList,
  generateAssessmentSchema,
  updateAssessmentSchema,
  createAssessmentType,
} from "../../../api/services/Assessment/InitalAssessment";

import ConfirmModal from "../../../components/ConfirmModal/ConfirmModal";
import {
  AssessmentCard,
  ConfigModal,
  CreateAssessmentTypeModal,
} from "../../../components/Clients/CarePlan/InitialAssessments";
import { useUserProfile } from "../../../hooks/userProfile";
import { USER_TYPES } from "../../../constants/userTypes";
import { usePermissions } from "../../../hooks/usePermissions";
import useClientDetails from "../../../hooks/useClientDetails";

const calculateAge = (birthdate) => {
  if (!birthdate) return null;
  const today = new Date();
  const dob = new Date(birthdate);
  if (isNaN(dob.getTime())) return null;
  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    age--;
  }
  return age;
};

const InitialAssessmentList = () => {
  const { clientID } = useParams();
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const recognitionRef = useRef(null);
  const { clientDetails, fullName } = useClientDetails(clientID);

  const [open, setOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [createTypeOpen, setCreateTypeOpen] = useState(false);
  const [selectedType, setSelectedType] = useState(null);

  const [isRecording, setIsRecording] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [generatedSchema, setGeneratedSchema] = useState(null);
  const { hasPermission, isLoading } = usePermissions();

  const { data, isLoading: permissionsLoading } = useQuery(
    ["initial-assessments", clientID],
    () => fetchInitialAssessmentList(clientID),
    { enabled: !!clientID }
  );

  const createTypeMutation = useMutation(createAssessmentType, {
    onSuccess: () => {
      queryClient.invalidateQueries(["initial-assessments", clientID]);
      setCreateTypeOpen(false);
      enqueueSnackbar("Assessment category created successfully", {
        variant: "success",
      });
    },
    onError: (error) => {
      enqueueSnackbar(error?.message || "Failed to create category", {
        variant: "error",
      });
    },
  });

  const updateSchemaMutation = useMutation(
    ({ clientId, typeId, schema }) =>
      updateAssessmentSchema(clientId, typeId, schema),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["initial-assessments", clientID]);
        enqueueSnackbar("Assessment framework updated", {
          variant: "success",
        });
        handleClose();
      },
    }
  );

  const handleOpenModal = (type) => {
    setSelectedType(type);
    if (type.is_configured) setConfirmOpen(true);
    else setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setConfirmOpen(false);
    setCreateTypeOpen(false);
    setTranscript("");
    setGeneratedSchema(null);
    stopRecognition();
  };

  const startRecognition = () => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;
    recognitionRef.current = new SpeechRecognition();
    recognitionRef.current.continuous = true;
    recognitionRef.current.lang = "en-GB";
    recognitionRef.current.onresult = (e) => {
      let final = "";
      for (let i = e.resultIndex; i < e.results.length; ++i) {
        if (e.results[i].isFinal) final += e.results[i][0].transcript;
      }
      setTranscript((prev) => prev + (prev ? " " : "") + final);
    };
    recognitionRef.current.onstart = () => setIsRecording(true);
    recognitionRef.current.onend = () => setIsRecording(false);
    recognitionRef.current.start();
  };

  const stopRecognition = () => recognitionRef.current?.stop();

  const handleGenerateSchema = async () => {
    setIsGenerating(true);
    try {
      const schema = await generateAssessmentSchema({
        type_id: selectedType.id,
        condition: transcript,
      });
      setGeneratedSchema(schema);
    } catch (err) {
      enqueueSnackbar("Failed to generate schema", { variant: "error" });
    } finally {
      setIsGenerating(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const getInitials = () => {
    if (!fullName) return "C";
    const names = fullName.split(" ");
    if (names.length === 1) return names[0].charAt(0).toUpperCase();
    return (
      names[0].charAt(0) + names[names.length - 1].charAt(0)
    ).toUpperCase();
  };

  const getPrimaryAddress = () => {
    if (!clientDetails?.addresses) return null;
    const primary = clientDetails.addresses.find(
      (addr) => addr.is_service_delivery === true
    );
    return primary || clientDetails.addresses[0];
  };

  const primaryAddress = getPrimaryAddress();
  const age = calculateAge(clientDetails?.birthdate);

  if (isLoading)
    return (
      <Box sx={{ p: 4 }}>
        <Skeleton
          variant="rectangular"
          height={100}
          sx={{ borderRadius: 3, mb: 3 }}
        />
        <Skeleton variant="text" width="200px" height={32} />
        <Skeleton
          variant="text"
          width="400px"
          height={24}
          sx={{ mb: 4 }}
        />
        <Grid container spacing={3}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Skeleton
                variant="rectangular"
                height={140}
                sx={{ borderRadius: 3 }}
              />
            </Grid>
          ))}
        </Grid>
      </Box>
    );

  return (
    <Box sx={{ maxWidth: 1400, mx: "auto", p: { xs: 2, sm: 3, md: 4 } }}>
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          p: 3,
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha(
            theme.palette.primary.main,
            0.05
          )} 0%, ${alpha(theme.palette.primary.light, 0.02)} 100%)`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          spacing={2}
        >
          <Stack direction="row" alignItems="center" spacing={2.5}>
            <Avatar
              src={clientDetails?.profile_photo}
              sx={{
                width: 64,
                height: 64,
                bgcolor: theme.palette.primary.main,
                fontSize: 24,
                fontWeight: 700,
                boxShadow: `0 4px 12px ${alpha(
                  theme.palette.primary.main,
                  0.3
                )}`,
              }}
            >
              {!clientDetails?.profile_photo && getInitials()}
            </Avatar>
            <Box>
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{ color: "#0F172A", mb: 0.5 }}
              >
                {fullName || "Client Name"}
              </Typography>
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                flexWrap="wrap"
                sx={{ gap: 1 }}
              >
                {clientDetails?.birthdate && (
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Cake sx={{ fontSize: 14, color: "#64748B" }} />
                    <Typography
                      variant="caption"
                      sx={{ color: "#64748B" }}
                    >
                      DOB: {formatDate(clientDetails.birthdate)}
                      {age !== null && ` (${age} yrs)`}
                    </Typography>
                  </Stack>
                )}
                {clientDetails?.nhs_number && (
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <BadgeIcon sx={{ fontSize: 14, color: "#64748B" }} />
                    <Typography
                      variant="caption"
                      sx={{ color: "#64748B" }}
                    >
                      NHS: {clientDetails.nhs_number}
                    </Typography>
                  </Stack>
                )}
                {clientDetails?.gender && (
                  <Typography
                    variant="caption"
                    sx={{ color: "#64748B" }}
                  >
                    • {clientDetails.gender}
                  </Typography>
                )}
                {primaryAddress && (
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <LocationOn sx={{ fontSize: 14, color: "#64748B" }} />
                    <Typography
                      variant="caption"
                      sx={{ color: "#64748B" }}
                      noWrap
                    >
                      {primaryAddress.address_1}
                      {primaryAddress.town
                        ? `, ${primaryAddress.town}`
                        : ""}
                    </Typography>
                  </Stack>
                )}
                <Chip
                  label={clientDetails?.status || "Active"}
                  size="small"
                  sx={{
                    height: 22,
                    fontSize: "0.65rem",
                    fontWeight: 600,
                    bgcolor:
                      clientDetails?.status === "Active"
                        ? "#D1FAE5"
                        : "#FEE2E2",
                    color:
                      clientDetails?.status === "Active"
                        ? "#039855"
                        : "#EF4444",
                    borderRadius: 1.5,
                  }}
                />
              </Stack>
            </Box>
          </Stack>
        </Stack>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, sm: 4 },
          borderRadius: 3,
          bgcolor: "#FFFFFF",
          border: `1px solid ${alpha(
            theme.palette.primary.main,
            0.06
          )}`,
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          sx={{ mb: 4, gap: 2 }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                bgcolor: alpha("#2469F0", 0.08),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <AssessmentOutlined
                sx={{ color: "#2469F0", fontSize: 28 }}
              />
            </Box>
            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: "#0F172A",
                  fontSize: { xs: "1.25rem", sm: "1.5rem" },
                  letterSpacing: "-0.02em",
                  mb: 0.5,
                }}
              >
                Initial Assessment Setup
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "#64748B",
                  fontSize: "0.9rem",
                  fontWeight: 400,
                }}
              >
                Generate person-centered assessment forms using AI to
                capture specific care needs.
              </Typography>
            </Box>
          </Stack>

          {!permissionsLoading &&
            hasPermission([USER_TYPES.ADMIN, USER_TYPES.MANAGER]) && (
              <Button
                variant="contained"
                startIcon={<AddOutlined />}
                onClick={() => setCreateTypeOpen(true)}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  px: 3,
                  py: 1.2,
                  bgcolor: "#2469F0",
                  color: "#FFFFFF",
                  boxShadow: `0 2px 8px ${alpha("#2469F0", 0.25)}`,
                  "&:hover": {
                    bgcolor: "#1A5BD4",
                    boxShadow: `0 4px 16px ${alpha("#2469F0", 0.35)}`,
                  },
                  "&:active": {
                    bgcolor: "#1553B8",
                  },
                }}
              >
                Add Assessment Type
              </Button>
            )}
        </Stack>

        {data?.assessment_types?.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: 8,
              px: 4,
              bgcolor: "#F8FAFC",
              borderRadius: 3,
              border: `2px dashed ${alpha("#94A3B8", 0.2)}`,
            }}
          >
            <AssessmentOutlined
              sx={{ fontSize: 64, color: "#94A3B8", mb: 2 }}
            />
            <Typography
              variant="h6"
              sx={{ color: "#0F172A", fontWeight: 600, mb: 1 }}
            >
              No Assessment Types Available
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "#94A3B8", mb: 2 }}
            >
              Click "Add Assessment Type" to create your first assessment
              category.
            </Typography>
            {!permissionsLoading &&
              hasPermission([USER_TYPES.ADMIN, USER_TYPES.MANAGER]) && (
                <Button
                  variant="contained"
                  startIcon={<AddOutlined />}
                  onClick={() => setCreateTypeOpen(true)}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600,
                    bgcolor: "#2469F0",
                    "&:hover": {
                      bgcolor: "#1A5BD4",
                    },
                  }}
                >
                  Create First Assessment Type
                </Button>
              )}
          </Box>
        ) : (
          <>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="#64748B">
                {data?.assessment_types?.length} assessment type
                {data?.assessment_types?.length > 1 ? "s" : ""} available
              </Typography>
            </Box>
            <Grid container spacing={3}>
              {data?.assessment_types?.map((type) => (
                <Grid item xs={12} sm={6} md={4} key={type.id}>
                  <AssessmentCard type={type} onConfigure={handleOpenModal} />
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </Paper>

      <CreateAssessmentTypeModal
        open={createTypeOpen}
        handleClose={() => setCreateTypeOpen(false)}
        isLoading={createTypeMutation.isLoading}
        onSave={(formData) => createTypeMutation.mutate(formData)}
      />

      <ConfirmModal
        show={confirmOpen}
        handleClose={() => setConfirmOpen(false)}
        handleConfirm={() => {
          setConfirmOpen(false);
          setOpen(true);
        }}
        title="Reconfigure Assessment?"
        body={`A custom framework for "${selectedType?.name}" already exists for this client. Overwrite it?`}
        confirmText="Yes, Start Reconfig"
      />

      <ConfigModal
        open={open}
        handleClose={handleClose}
        selectedType={selectedType}
        transcript={transcript}
        setTranscript={setTranscript}
        isRecording={isRecording}
        startRecording={startRecognition}
        stopRecording={stopRecognition}
        isGenerating={isGenerating}
        handleGenerate={handleGenerateSchema}
        generatedSchema={generatedSchema}
        setGeneratedSchema={setGeneratedSchema}
        onSave={() =>
          updateSchemaMutation.mutate({
            clientId: clientID,
            typeId: selectedType.id,
            schema: generatedSchema,
          })
        }
        isSaving={updateSchemaMutation.isLoading}
      />
    </Box>
  );
};

export default InitialAssessmentList;